import type { BankedProfile } from "../simulation/persistence";
import type { WorldSnapshot } from "../simulation/snapshot";
import { activeSortieContract, contractRunSummary } from "../simulation/sortie-contracts";
import { BUILD_SHORT_SHA } from "../build-info";
import { buildShipStatusHtml } from "./ship-status";

export type PauseStatusHandlers = {
  onOpenSettings?: () => void;
  onResume?: () => void;
};

/**
 * Mid-expedition pause: loadout readout only.
 * Graphics / audio live on the dedicated settings overlay.
 */
export class PauseStatusPanel {
  private readonly root: HTMLElement;
  private lastKey = "";
  private lastZoneKey = "";

  public constructor(
    host: HTMLElement,
    private readonly handlers: PauseStatusHandlers = {},
  ) {
    this.root = document.createElement("aside");
    this.root.className = "pause-status";
    this.root.hidden = true;
    this.root.innerHTML = `
      <header>
        <strong>Paused — Ship Status</strong>
        <span class="pause-hint">P / II to resume · BUILD ${BUILD_SHORT_SHA}</span>
      </header>
      <div data-zone class="pause-zone"></div>
      <div class="pause-actions">
        <button type="button" class="hangar-secondary" data-open-settings>Systems</button>
        <button type="button" class="launch-btn" data-resume>Resume</button>
      </div>
      <div data-body class="ship-status"></div>
    `;
    host.appendChild(this.root);
    this.root.querySelector("[data-resume]")?.addEventListener("click", () => {
      this.handlers.onResume?.();
    });
    this.root.querySelector("[data-open-settings]")?.addEventListener("click", () => {
      this.handlers.onOpenSettings?.();
    });
  }

  public sync(profile: BankedProfile, snap: WorldSnapshot): void {
    const show = snap.paused && snap.phase !== "carrier" && snap.phase !== "extracted";
    this.root.hidden = !show;
    if (!show) {
      this.lastKey = "";
      return;
    }

    const zoneKey = `${snap.biomeName}|${snap.dangerLabel}|${snap.dangerTier}|${snap.threatBudget}|${snap.threatBudgetMax}|${snap.threatGrade}|${snap.chunkHazard.toFixed(2)}|${snap.activeSortieContract ?? ""}`;
    if (zoneKey !== this.lastZoneKey) {
      const zone = this.root.querySelector("[data-zone]");
      if (zone) {
        const contract = activeSortieContract(snap.activeSortieContract);
        zone.innerHTML = `
          <span data-danger="${snap.dangerTier}">${snap.biomeName} · ${snap.dangerLabel}</span>
          <em>Threat ${snap.threatBudget}/${snap.threatBudgetMax} · G${snap.threatGrade} · hazard ${(snap.chunkHazard * 100).toFixed(0)}%</em>
          ${contract ? `<strong>${contract.name.toUpperCase()} · ${contractRunSummary(contract)}</strong>` : ""}
        `;
      }
      this.lastZoneKey = zoneKey;
    }

    const key = [
      profile.selectedFrameId,
      profile.selectedWeapons.join(","),
      snap.runInventory.affixes.join(","),
      snap.runInventory.components.join(","),
      snap.runProgress.takenNodes.join(","),
      snap.cosmeticsTrailId,
    ].join("|");
    if (key === this.lastKey) return;
    this.lastKey = key;
    const body = this.root.querySelector("[data-body]");
    if (body) body.innerHTML = buildShipStatusHtml(profile, snap);
  }

  public dispose(): void {
    this.root.remove();
  }
}
